// ============================================
// Financial Configuration Panel
//
// Shows Stripe, QBO, and Sync Rule generation
// status for the active brand. Allows (re)generation
// via the generateFinancialConfig Cloud Function.
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, FileText, RefreshCw, Settings, Loader2 } from 'lucide-react';
import { db } from '../../../services/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { C_DOC_BRANDS, SC_STRIPE_CONFIG, SC_QBO_CONFIG, SC_SYNC_RULES } from '../../../constants/collections';
import { generateFinancialConfig } from '../../../services/financial/financialService';

interface ConfigStatus {
  stripeProducts: number | null;
  qboAccounts: number | null;
  syncRules: number | null;
  lastGenerated: string | null;
}

const INITIAL_STATUS: ConfigStatus = {
  stripeProducts: null,
  qboAccounts: null,
  syncRules: null,
  lastGenerated: null,
};

export function ConfigPanel() {
  const [brandId, setBrandId] = useState<string | null>(null);
  const [status, setStatus] = useState<ConfigStatus>(INITIAL_STATUS);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch config status counts from Firestore sub-collections
  const fetchStatus = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const result: ConfigStatus = { ...INITIAL_STATUS };

      // Check stripe_config document
      const stripeRef = doc(db, C_DOC_BRANDS, id, SC_STRIPE_CONFIG, 'current');
      const stripeSnap = await getDoc(stripeRef);
      if (stripeSnap.exists()) {
        const data = stripeSnap.data();
        result.stripeProducts = data.products?.length ?? 0;
        result.lastGenerated = data.generatedAt ?? null;
      }

      // Check qbo_config document
      const qboRef = doc(db, C_DOC_BRANDS, id, SC_QBO_CONFIG, 'current');
      const qboSnap = await getDoc(qboRef);
      if (qboSnap.exists()) {
        const data = qboSnap.data();
        result.qboAccounts = data.accounts?.length ?? 0;
        if (!result.lastGenerated && data.generatedAt) {
          result.lastGenerated = data.generatedAt;
        }
      }

      // Count sync_rules documents
      const syncSnap = await getDocs(collection(db, C_DOC_BRANDS, id, SC_SYNC_RULES));
      if (!syncSnap.empty) {
        result.syncRules = syncSnap.size;
      }

      setStatus(result);
    } catch (err) {
      console.error('Failed to fetch financial config status:', err);
      setError('Failed to load configuration status');
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount: get the first brand, then fetch its config status
  useEffect(() => {
    async function init() {
      try {
        const brandsSnap = await getDocs(collection(db, C_DOC_BRANDS));
        if (brandsSnap.empty) {
          setError('No brands found');
          setLoading(false);
          return;
        }
        const firstBrand = brandsSnap.docs[0];
        setBrandId(firstBrand.id);
        await fetchStatus(firstBrand.id);
      } catch (err) {
        console.error('Failed to load brands:', err);
        setError('Failed to load brand data');
        setLoading(false);
      }
    }
    init();
  }, [fetchStatus]);

  // Generate or regenerate financial config
  const handleGenerate = async () => {
    if (!brandId) return;
    setGenerating(true);
    setError(null);

    try {
      await generateFinancialConfig(brandId);
      await fetchStatus(brandId);
    } catch (err) {
      console.error('Failed to generate financial config:', err);
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const hasExistingConfig =
    status.stripeProducts !== null ||
    status.qboAccounts !== null ||
    status.syncRules !== null;

  const formatTimestamp = (iso: string | null): string => {
    if (!iso) return 'Never';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return iso;
    }
  };

  const cards: {
    label: string;
    value: number | null;
    icon: typeof CreditCard;
    colorClass: string;
    bgClass: string;
  }[] = [
    {
      label: 'Stripe Products',
      value: status.stripeProducts,
      icon: CreditCard,
      colorClass: 'text-purple-400',
      bgClass: 'bg-purple-500/10',
    },
    {
      label: 'QBO Accounts',
      value: status.qboAccounts,
      icon: FileText,
      colorClass: 'text-blue-400',
      bgClass: 'bg-blue-500/10',
    },
    {
      label: 'Sync Rules',
      value: status.syncRules,
      icon: RefreshCw,
      colorClass: 'text-green-400',
      bgClass: 'bg-green-500/10',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-bg-card border border-bg-tertiary rounded-xl p-5"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center">
          <Settings size={18} className="text-gold" />
        </div>
        <h2 className="text-lg font-display font-semibold text-text-primary">
          Financial Configuration
        </h2>
      </div>
      <p className="text-sm text-text-muted mb-5">
        Last generated: {formatTimestamp(status.lastGenerated)}
      </p>

      {/* Status Cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {cards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08, duration: 0.3 }}
            className="bg-bg-elevated rounded-lg p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <div
                className={`w-8 h-8 rounded-lg ${card.bgClass} flex items-center justify-center`}
              >
                <card.icon size={16} className={card.colorClass} />
              </div>
              <span className="text-sm text-text-secondary font-medium">
                {card.label}
              </span>
            </div>
            <p className={`text-2xl font-bold ${card.colorClass}`}>
              {loading ? '--' : card.value !== null ? card.value : 'Not generated'}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={generating || !brandId}
        className={`px-5 py-3 bg-gold text-bg-deep rounded-lg font-medium hover:bg-gold/90 transition-colors ${
          generating || !brandId ? 'opacity-60 cursor-not-allowed' : ''
        }`}
      >
        {generating ? (
          <span className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            Generating...
          </span>
        ) : hasExistingConfig ? (
          'Regenerate Financial Config'
        ) : (
          'Generate Financial Config'
        )}
      </button>

      {/* Note */}
      <p className="text-sm text-text-muted mt-4">
        Stripe products will be created in your Stripe account. QBO template is
        stored locally and can be exported from the CFO view.
      </p>
    </motion.div>
  );
}
